/**
 * VirtHypervisorsList — Hypervisores (estándar de listados del CMDB).
 *
 * Antes: tabla propia (`wol-table`) con tema oscuro a mano, emojis por tipo,
 * diálogo de borrado propio y la ficha abriendo **directamente en edición**.
 *
 * Ahora: `ListPage` con buscador, filtros, columnas, vistas y export; la ficha
 * entra en **lectura** (con botón Editar) y el borrado usa el diálogo único.
 *
 * Botones propios:
 *  · **Ver sus VMs** — salta a Máquinas virtuales con el hipervisor ya filtrado
 *    (`?hypervisorId=`, que la lista de VMs lee).
 *  · **Ver sus redes virtuales** — a vSwitches con `?hypervisorId=`.
 *  · **Ver el servidor físico** — a la ficha del CI (`docElementPath()`).
 *  · **Ver el cluster** — resalta el cluster al que pertenece el host
 *    (`/virt/clusters?destacar=&abrir=1`).
 *  · **Copiar** — alta precargada sin IP de gestión, cluster ni agente.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CpuChipIcon,
  ListBulletIcon,
  Squares2X2Icon,
  ComputerDesktopIcon,
  ArrowsRightLeftIcon,
  ServerStackIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import VirtHypervisorForm from './VirtHypervisorForm';
import { useListView } from '../views/ViewsContext';
import useNavOrder from '../hooks/useNavOrder';
import { docElementPath } from '../utils/docElements';
import { getServerFormStyle } from '../utils/serverFormStyle';
import api from '../services/api';

const STATUS_META = {
  active:      { label: 'Activo',        badge: 'list-badge--on' },
  maintenance: { label: 'Mantenimiento', badge: 'list-badge--warn' },
  decom:       { label: 'Decom',         badge: 'list-badge--off' },
};

const AGENT_META = {
  online:     { label: 'Agente online',   badge: 'list-badge--on' },
  offline:    { label: 'Agente offline',  badge: 'list-badge--off' },
  'no-agent': { label: 'Sin agente',      badge: 'list-badge--warn' },
  unknown:    { label: 'Sin datos',       badge: 'list-badge' },
};

/** Etiquetas por si el backend todavía no manda `hypervisor_type_label`. */
const HYP_TYPE_LABEL = {
  vmware_esxi: 'VMware ESXi',
  proxmox:     'Proxmox VE',
  hyper_v:     'Hyper-V',
  kvm:         'KVM / Libvirt',
  nutanix_ahv: 'Nutanix AHV',
  xen:         'Xen / XCP-ng',
  other:       'Otro',
};

const EMPTY_HYP = {
  name: '', hypervisor_type: 'vmware_esxi', version: '', mgmt_ip: '', mgmt_url: '',
  cpu_total_cores: '', cpu_model: '', ram_total_gb: '', storage_local_tb: '',
  ha_mode: 'standalone', status: 'active', rack_element_id: '', notes: '',
};

export default function VirtHypervisorsList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');

  const [filterType, setFilterType]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterServer, setFilterServer] = useState('');
  const [filterAgent, setFilterAgent]   = useState('');
  const [filterVms, setFilterVms]       = useState('');
  const [filterCluster, setFilterCluster] = useState('');
  const [groupByServer, setGroupByServer] = useState(false);

  const [selected, setSelected]         = useState(null);   // null = lista
  const [initialMode, setInitialMode]   = useState('edit');
  const [cloneSource, setCloneSource]   = useState(null);
  const [hlId, setHlId]                 = useState(null);

  /** Llega desde el listado de clusters («Ver sus hipervisores»). */
  const qCluster = searchParams.get('clusterId');
  useEffect(() => {
    if (qCluster) setFilterCluster(String(qCluster));
  }, [qCluster]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.getHypervisors(companyId);
      setItems(res.data || []);
    } catch (e) {
      setError(e.message || 'Error cargando los hipervisores');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ?destacar=<id>[&abrir=1] — convención del CMDB (el Diseñador de servicios y
     las listas enlazadas llegan así). */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !items.length) return undefined;
    const id = Number(raw);
    const target = items.find(i => i.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setCloneSource(null); setInitialMode('view'); setSelected(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`virt-hyp-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, items, setSearchParams]);

  /* ── ficha ──────────────────────────────────────────────────────────────── */
  const openView   = (h) => { setCloneSource(null); setInitialMode('view');   setSelected(h); };
  const openEdit   = (h) => { setCloneSource(null); setInitialMode('edit');   setSelected(h); };
  const openCreate = ()  => { setCloneSource(null); setInitialMode('create'); setSelected({ ...EMPTY_HYP }); };
  const openCopy   = (h) => {
    if (!h) return;
    setSelected(null);
    setCloneSource({
      ...h,
      id: undefined,
      name: h.name ? `${h.name} (copia)` : '',
      mgmt_ip: '',
      mgmt_url: '',
      rack_element_id: h.rack_element_id || '',
      agent_id: null,
      agent_status: 'unknown',
      cluster_id: undefined,
      cluster_name: undefined,
    });
    setInitialMode('create');
  };
  const closeForm = () => { setSelected(null); setCloneSource(null); load(); };

  const handleDelete = async (h) => {
    try {
      await api.deleteHypervisor(h.id, companyId);
      await load();
    } catch (e) {
      setError(e.message || 'Error eliminando el hipervisor');
    }
  };

  /* ── opciones de los filtros (derivadas de los datos) ───────────────────── */
  const typeOptions = useMemo(() => {
    const seen = new Map();
    items.forEach((h) => {
      if (!h.hypervisor_type) return;
      seen.set(h.hypervisor_type, h.hypervisor_type_label || HYP_TYPE_LABEL[h.hypervisor_type] || h.hypervisor_type);
    });
    Object.entries(HYP_TYPE_LABEL).forEach(([value, label]) => {
      if (!seen.has(value)) seen.set(value, label);
    });
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [items]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((h) => {
      if (filterType && h.hypervisor_type !== filterType) return false;
      if (filterStatus && (h.status || 'active') !== filterStatus) return false;
      if (filterServer === 'with' && !h.rack_element_id) return false;
      if (filterServer === 'without' && h.rack_element_id) return false;
      if (filterAgent) {
        const st = h.agent_status || 'unknown';
        if (filterAgent === 'with' && (!st || st === 'no-agent' || st === 'unknown')) return false;
        if (filterAgent === 'without' && st !== 'no-agent') return false;
        if (filterAgent === 'online' && st !== 'online') return false;
        if (filterAgent === 'offline' && st !== 'offline') return false;
      }
      if (filterVms === 'with' && !(h.vm_count > 0)) return false;
      if (filterVms === 'without' && h.vm_count > 0) return false;
      if (filterCluster && String(h.cluster_id || '') !== String(filterCluster)) return false;
      if (!needle) return true;
      return [
        h.name, h.mgmt_ip, h.mgmt_url, h.version, h.cpu_model,
        h.hypervisor_type_label || HYP_TYPE_LABEL[h.hypervisor_type] || h.hypervisor_type,
        h.rack_element_name, h.rack_element_ip, h.cluster_name,
      ].filter(Boolean).join(' ').toLowerCase().includes(needle);
    });
  }, [items, search, filterType, filterStatus, filterServer, filterAgent, filterVms, filterCluster]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navHypervisors = useNavOrder(filtered, selected, selected !== null);

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: tr('virt.name', 'Nombre'),
        sortable: true,
        render: (h) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>{h.name}</span>
            <span className="list-hint">{h.version ? `v${h.version}` : ''}</span>
          </span>
        ),
      },
      {
        key: 'hypervisor_type',
        label: tr('virt.type', 'Tipo'),
        sortable: true,
        sortValue: (h) => h.hypervisor_type || '',
        render: (h) => (
          <span className="list-badge">
            {h.hypervisor_type_label || HYP_TYPE_LABEL[h.hypervisor_type] || h.hypervisor_type || dash}
          </span>
        ),
      },
      {
        key: 'server',
        label: tr('virt.physical_server', 'Servidor físico'),
        sortable: true,
        sortValue: (h) => h.rack_element_name || '',
        render: (h) => {
          if (!h.rack_element_name && !h.rack_element_ip) return dash;
          return (
            <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <span>{h.rack_element_name || dash}</span>
              {h.rack_element_ip && <span className="list-code list-hint">{h.rack_element_ip}</span>}
            </span>
          );
        },
      },
      {
        key: 'mgmt_ip',
        label: tr('virt.mgmt_ip', 'IP de gestión'),
        sortable: true,
        render: (h) => (h.mgmt_ip ? <span className="list-code">{h.mgmt_ip}</span> : dash),
      },
      {
        key: 'capacity',
        label: tr('virt.capacity', 'CPU / RAM'),
        sortable: true,
        sortValue: (h) => h.cpu_total_cores ?? 0,
        render: (h) => (
          h.cpu_total_cores || h.ram_total_gb
            ? (
              <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
                <span>{h.cpu_total_cores ? `${h.cpu_total_cores} cores` : ''}</span>
                <span className="list-hint">{h.ram_total_gb ? `${h.ram_total_gb} GB` : ''}</span>
              </span>
            )
            : dash
        ),
      },
      {
        key: 'storage_local_tb',
        label: tr('virt.local_storage', 'Almacenamiento local'),
        sortable: true,
        sortValue: (h) => h.storage_local_tb ?? 0,
        render: (h) => (h.storage_local_tb ? `${h.storage_local_tb} TB` : dash),
      },
      {
        key: 'cluster',
        label: tr('virt.cluster', 'Cluster'),
        sortable: true,
        sortValue: (h) => h.cluster_name || '',
        render: (h) => (h.cluster_name
          ? (
            <button
              type="button"
              className="list-badge"
              style={{ cursor: 'pointer' }}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/virt/clusters?destacar=${h.cluster_id}&abrir=1`);
              }}
              title={tr('virt.view_cluster', 'Ver el cluster al que pertenece')}
            >
              {h.cluster_name}
            </button>
          )
          : <span className="list-hint">{tr('virt.no_cluster', 'Suelto')}</span>),
      },
      {
        key: 'vm_count',
        label: tr('virt.vms', 'VMs'),
        sortable: true,
        sortValue: (h) => h.vm_count ?? 0,
        render: (h) => (
          <button
            type="button"
            className="list-badge"
            style={{ cursor: h.vm_count ? 'pointer' : 'default' }}
            disabled={!h.vm_count}
            onClick={(event) => {
              if (!h.vm_count) return;
              event.stopPropagation();
              navigate(`/virt/vms?hypervisorId=${h.id}`);
            }}
            title={tr('virt.view_vms', 'Ver sus VMs')}
          >
            {h.vm_count || 0} VMs
          </button>
        ),
      },
      {
        key: 'agent_status',
        label: tr('virt.agent', 'Agente'),
        sortable: true,
        sortValue: (h) => h.agent_status || 'unknown',
        render: (h) => {
          const meta = AGENT_META[h.agent_status || 'unknown'] || AGENT_META.unknown;
          return (
            <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
              <span className={`list-badge ${meta.badge}`}>{meta.label}</span>
              {h.last_seen_at && (
                <span className="list-hint">{new Date(h.last_seen_at).toLocaleString()}</span>
              )}
            </span>
          );
        },
      },
      {
        key: 'status',
        label: tr('ipam.status', 'Estado'),
        sortable: true,
        sortValue: (h) => h.status || 'active',
        render: (h) => {
          const meta = STATUS_META[h.status] || STATUS_META.active;
          return <span className={`list-badge ${meta.badge}`}>{meta.label}</span>;
        },
      },
      {
        key: 'notes',
        label: tr('description', 'Notas'),
        render: (h) => h.notes || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, navigate]);

  const defaultVisible = ['name', 'hypervisor_type', 'server', 'mgmt_ip', 'capacity', 'vm_count', 'cluster', 'status'];

  const filters = [
    {
      key: 'type', label: tr('virt.type', 'Tipo'), allLabel: tr('virt.type_all', 'Todos los tipos'),
      value: filterType, onChange: setFilterType, options: typeOptions,
    },
    {
      key: 'status', label: tr('ipam.status', 'Estado'), allLabel: tr('ipam.status_all', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'server', label: tr('virt.physical_server', 'Servidor físico'), allLabel: tr('virt.server_all', 'Cualquier servidor'),
      value: filterServer, onChange: setFilterServer,
      options: [
        { value: 'with', label: tr('virt.server_with', 'Con servidor asignado') },
        { value: 'without', label: tr('virt.server_without', 'Sin servidor asignado') },
      ],
    },
    {
      key: 'agent', label: tr('virt.agent', 'Agente'), allLabel: tr('virt.agent_all', 'Cualquier agente'),
      value: filterAgent, onChange: setFilterAgent,
      options: [
        { value: 'with', label: tr('virt.agent_with', 'Con agente instalado') },
        { value: 'online', label: tr('virt.agent_online', 'Agente online') },
        { value: 'offline', label: tr('virt.agent_offline', 'Agente offline') },
        { value: 'without', label: tr('virt.agent_without', 'Sin agente') },
      ],
    },
    {
      key: 'vms', label: tr('virt.vms', 'VMs'), allLabel: tr('virt.vms_all', 'Cualquier número de VMs'),
      value: filterVms, onChange: setFilterVms,
      options: [
        { value: 'with', label: tr('virt.vms_with', 'Con VMs') },
        { value: 'without', label: tr('virt.vms_without', 'Sin VMs') },
      ],
    },
    {
      key: 'group-server',
      type: 'toggle',
      label: tr('virt.group_by_server', 'Agrupar por servidor'),
      value: groupByServer,
      onChange: (next) => setGroupByServer(Boolean(next)),
      title: tr('virt.group_by_server_hint', 'Ver los hipervisores agrupados por servidor físico'),
    },
  ];

  const subtitle = `${items.length} ${tr('virt.hyp_plural', 'hipervisores')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtrados')}`;

  const exportRows = useMemo(() => filtered.map(h => ({
    'Nombre': h.name || '',
    'Tipo': h.hypervisor_type_label || HYP_TYPE_LABEL[h.hypervisor_type] || h.hypervisor_type || '',
    'Versión': h.version || '',
    'Servidor físico': h.rack_element_name || '',
    'IP del servidor': h.rack_element_ip || '',
    'IP de gestión': h.mgmt_ip || '',
    'URL de gestión': h.mgmt_url || '',
    'Cluster': h.cluster_name || '',
    'Rol en el cluster': h.member_role || '',
    'Modelo de CPU': h.cpu_model || '',
    'CPU (cores)': h.cpu_total_cores ?? '',
    'RAM (GB)': h.ram_total_gb ?? '',
    'Almacenamiento local (TB)': h.storage_local_tb ?? '',
    'Modo HA': h.ha_mode || '',
    'VMs': h.vm_count ?? 0,
    'Agente': (AGENT_META[h.agent_status || 'unknown'] || AGENT_META.unknown).label,
    'Última vez visto': h.last_seen_at ? new Date(h.last_seen_at).toLocaleString() : '',
    'Versión del agente': h.agent_version || '',
    'Estado': (STATUS_META[h.status] || STATUS_META.active).label,
  })), [filtered]);

  /** Tarjetas: lo esencial del hipervisor, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((h) => {
          const meta = STATUS_META[h.status] || STATUS_META.active;
          return (
            <div
              key={h.id}
              id={`virt-hyp-card-${h.id}`}
              className={`list-card${h.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openView(h)}
              onKeyDown={(event) => { if (event.key === 'Enter') openView(h); }}
              title={`${tr('action.view', 'Ver')} ${h.name}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">{h.name}</div>
                  {h.mgmt_ip && <span className="list-code">{h.mgmt_ip}</span>}
                </div>
                <span className={`list-badge ${meta.badge}`}>{meta.label}</span>
              </div>

              <div className="list-card-meta">
                <span className="list-badge">
                  {h.hypervisor_type_label || HYP_TYPE_LABEL[h.hypervisor_type] || h.hypervisor_type}
                </span>
                {h.rack_element_name && <span>{h.rack_element_name}</span>}
                {h.cpu_total_cores ? <span>{h.cpu_total_cores} cores</span> : null}
                {h.ram_total_gb ? <span>{h.ram_total_gb} GB RAM</span> : null}
                {h.cluster_name && <span className="list-badge">{h.cluster_name}</span>}
                <span className="list-badge">{h.vm_count || 0} VMs</span>
              </div>

              <div className="list-card-actions">{rowActions(h)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  const HYP_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada hipervisor'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId]);
  useListView('virt_hypervisors', 'list');

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          {tr('virt.select_company', 'Selecciona una empresa para ver la virtualización.')}
        </div>
      </div>
    );
  }

  return (
    <>
      {(selected !== null || cloneSource) && (
        <VirtHypervisorForm
          hypervisor={cloneSource || (selected && selected.id ? selected : null)}
          companyId={companyId}
          initialMode={cloneSource ? 'create' : initialMode}
          variant={getServerFormStyle() === 'cards' ? 'cards' : 'classic'}
          navItems={navHypervisors}
          navIndex={!cloneSource && selected && selected.id ? navHypervisors.findIndex(h => String(h.id) === String(selected.id)) : -1}
          onNavigate={(destino) => setSelected(destino)}
          onClose={closeForm}
          onSave={(creado) => {
            load();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setInitialMode('edit'); setSelected(creado); }
          }}
        />
      )}

      <div style={{ display: (selected !== null || cloneSource) ? 'none' : undefined }}>
        <ListPage
          icon={CpuChipIcon}
          title={tr('virt.hyp_title', 'Hipervisores')}
          subtitle={subtitle}

          onCreate={openCreate}
          createLabel={tr('virt.new_hyp', 'Nuevo hipervisor')}
          createTitle={tr('virt.new_hyp_hint', 'Host físico de virtualización (ESXi, Proxmox, Hyper-V…)')}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('virt.hyp_search', 'Buscar por nombre, IP, tipo, servidor, cluster…')}
          filters={filters}

          exportRows={exportRows}
          exportFileBase="hypervisores"
          exportSheetName="Hipervisores"

          columns={columns}
          columnStorageKey="cmdb_cols_virt_hypervisors"
          companyId={companyId}
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(h) => h.id}
          rowId={(h) => `virt-hyp-row-${h.id}`}
          rowClassName={(h) => (h.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={openView}
          rowTitle={(h) => `${tr('action.view', 'Ver')} ${h.name}`}
          groupBy={groupByServer
            ? (h) => h.rack_element_name || tr('virt.no_server', 'Sin servidor físico')
            : undefined}

          views={HYP_VIEWS}
          listKey="virt_hypervisors"
          defaultView="list"

          headerActions={qCluster && (
            <button
              type="button"
              className="list-btn-secondary"
              onClick={() => {
                setFilterCluster('');
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete('clusterId');
                  return next;
                }, { replace: true });
              }}
              title={tr('virt.clear_filter', 'Quitar este filtro')}
            >
              × {tr('virt.filtered_by_cluster', 'Solo este cluster')}
            </button>
          )}

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando hipervisores…')}
          error={error}

          empty={{
            icon: '🧊',
            title: items.length === 0
              ? tr('virt.hyp_empty', 'No hay hipervisores')
              : tr('msg.no_results', 'Ningún hipervisor coincide con los filtros'),
            subtitle: items.length === 0
              ? tr('virt.hyp_empty_hint', 'Registra los hosts de virtualización para poder asociarles VMs y redes.')
              : undefined,
            action: items.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={openCreate}>
                ＋ {tr('virt.new_hyp', 'Nuevo hipervisor')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (h) => {
              const serverType = h.rack_element_type === 'server' ? 'server' : 'rack_element';
              const serverPath = h.rack_element_id ? docElementPath(serverType, h.rack_element_id) : null;
              return (
                <>
                  {/* Ver sus VMs */}
                  <button
                    type="button"
                    className="list-action-btn"
                    disabled={!(h.vm_count > 0)}
                    onClick={() => navigate(`/virt/vms?hypervisorId=${h.id}`)}
                    title={tr('virt.view_vms', 'Ver sus VMs')}
                  >
                    <ComputerDesktopIcon />
                  </button>
                  {/* Ver sus redes virtuales */}
                  <button
                    type="button"
                    className="list-action-btn"
                    onClick={() => navigate(`/virt/vswitches?hypervisorId=${h.id}`)}
                    title={tr('virt.view_vswitches', 'Ver sus redes virtuales')}
                  >
                    <ArrowsRightLeftIcon />
                  </button>
                  {/* Ver el servidor físico */}
                  <button
                    type="button"
                    className="list-action-btn"
                    disabled={!serverPath}
                    onClick={() => serverPath && navigate(serverPath)}
                    title={h.rack_element_name
                      ? `${tr('virt.view_server', 'Ver el servidor físico')}: ${h.rack_element_name}`
                      : tr('virt.no_server', 'Sin servidor físico asignado')}
                  >
                    <ServerStackIcon />
                  </button>
                  {/* Ver el cluster */}
                  <button
                    type="button"
                    className="list-action-btn"
                    disabled={!h.cluster_id}
                    onClick={() => navigate(`/virt/clusters?destacar=${h.cluster_id}&abrir=1`)}
                    title={h.cluster_name
                      ? `${tr('virt.view_cluster', 'Ver el cluster al que pertenece')}: ${h.cluster_name}`
                      : tr('virt.no_cluster', 'Suelto')}
                  >
                    <CircleStackIcon />
                  </button>
                </>
              );
            },
            onCopy: openCopy,
            onEdit: openEdit,
            onDelete: handleDelete,
            deleteTitle: tr('virt.hyp_delete_title', 'Eliminar hipervisor'),
            deleteMessage: (h) => (
              <>
                {tr('virt.delete_confirm', '¿Seguro que quieres eliminar')} <strong>{h.name}</strong>?{' '}
                {h.vm_count > 0
                  ? tr('virt.hyp_delete_used', `Ojo: tiene ${h.vm_count} VM(s), que se quedarán sin hipervisor.`)
                  : tr('ip.delete_warn', 'No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>
    </>
  );
}
